import React, { useState, useEffect, useRef } from 'react'
import styles from './ToolsFilters.module.css'
import { Tag } from '../../../types/types'
import { Environment } from '../../../types/environment'
import GeneralFilter from '../../../ui/GeneralFilter/GeneralFilter'
import { Filter } from 'lucide-react'
import { useConnectionsStore } from '../../../store/connectionsStore'
import SavedFiltersSelect from './SavedFiltersSelect'
import SavedFiltersButton from './SavedFiltersButton'

export interface ToolsFiltersProps {
    statusFilter: 'all' | 'enabled' | 'disabled' | 'missing' | 'in_progress'
    onStatusChange: (v: 'all' | 'enabled' | 'disabled' | 'missing' | 'in_progress') => void
    methodFilter: 'all' | 'get' | 'post' | 'put' | 'delete' | 'patch'
    onMethodChange: (v: 'all' | 'get' | 'post' | 'put' | 'delete' | 'patch') => void
    connectionFilter?: string[] | null
    onConnectionChange: (v: string[] | null) => void
    environmentFilter: Environment | 'all'
    onEnvironmentChange: (v: Environment | 'all') => void
    selectedTags: string[]
    onSelectedTagsChange: (tags: string[]) => void
    tags: Tag[]
}

const ToolsFilters: React.FC<ToolsFiltersProps> = ({
    connectionFilter,
    onConnectionChange,
    statusFilter,
    onStatusChange,
    methodFilter,
    onMethodChange,
    environmentFilter,
    onEnvironmentChange,
    selectedTags,
    onSelectedTagsChange,
    tags,
}) => {
    const [showFilters, setShowFilters] = useState(false)
    const [pendingTags, setPendingTags] = useState<string[]>(selectedTags)
    const wrapperRef = useRef<HTMLDivElement | null>(null)
    const { connectors } = useConnectionsStore(state => state)

    useEffect(() => {
        setPendingTags(selectedTags)
    }, [selectedTags])

    // Close dropdown when clicking outside
    useEffect(() => {
        if (!showFilters) return
        const handleClick = (e: MouseEvent) => {
            if (!wrapperRef.current) return
            if (!wrapperRef.current.contains(e.target as Node)) {
                setShowFilters(false)
            }
        }
        document.addEventListener('mousedown', handleClick)
        return () => document.removeEventListener('mousedown', handleClick)
    }, [showFilters])

    const activeFilterCount = (() => {
        let connectionCount = 0;
        if (connectionFilter && connectionFilter.length > 0) {
            if (!(connectionFilter.length === 1 && connectionFilter[0] === 'all')) {
                connectionCount = connectionFilter.length;
            }
        }
        const tagCount = selectedTags.length > 0 ? 1 : 0
        const statusCount = statusFilter !== 'all' ? 1 : 0
        const methodCount = methodFilter !== 'all' ? 1 : 0
        const environmentCount = environmentFilter !== 'all' ? 1 : 0
        return connectionCount + tagCount + statusCount + methodCount + environmentCount 
    })()

    return (
        <div className={styles.filtersWrapper} ref={wrapperRef}>
            <button
                type="button"
                className={styles.filterButton}
                onClick={() => setShowFilters(f => !f)}
            >
                <Filter size={16} />
                {activeFilterCount > 0 && (
                    <span className={styles.filterCountBadge}>{activeFilterCount}</span>
                )}
            </button>
            {showFilters && (
                <div className={styles.filtersDropdown}>
                    <GeneralFilter
                        label="Connectors:"
                        value={connectionFilter || 'all'}
                        options={[ { value: 'all', label: 'All' }, ...connectors.filter(p => p.isActive).map(p => ({ value: p.id, label: p.connectionName }))]}
                        onChange={v => onConnectionChange(v as any)}
                        className={styles.methodFilter}
                        closeOnSelect={false}
                        multiSelect={true}
                    />
                    <GeneralFilter
                        label="Status:"
                        value={statusFilter}
                        options={[
                            { value: 'all', label: 'All' },
                            { value: 'enabled', label: 'Enabled' },
                            { value: 'disabled', label: 'Disabled' },
                            { value: 'missing', label: 'Missing' },
                            { value: 'in_progress', label: 'In Progress' },
                        ]}
                        onChange={v => onStatusChange(v as any)}
                        className={styles.statusFilter}
                        closeOnSelect={false}
                    />
                    <GeneralFilter
                        label="Environment:"
                        value={environmentFilter}
                        options={[
                            { value: 'all', label: 'All' },
                            { value: 'prod', label: 'Prod' },
                            { value: 'test', label: 'Test' },
                            { value: 'dev', label: 'Dev' },
                        ]}
                        onChange={v => onEnvironmentChange(v as any)}
                        className={styles.methodFilter}
                        closeOnSelect={false}
                    />
                    <GeneralFilter
                        label="Method:"
                        value={methodFilter}
                        options={[
                            { value: 'all', label: 'All' },
                            { value: 'get', label: 'GET' },
                            { value: 'post', label: 'POST' },
                            { value: 'put', label: 'PUT' },
                            { value: 'delete', label: 'DELETE' },
                        ]}
                        onChange={v => onMethodChange(v as any)}
                        className={styles.methodFilter}
                        closeOnSelect={false}
                    />
                    {tags.length > 0 && (
                        <div>
                            <div className={styles.filtersSectionTitle}>Tags</div>
                            <div className={styles.tagCheckboxList}>
                                {tags.map(t => {
                                    const checked = pendingTags.includes(t.name)
                                    return (
                                        <label key={t.name} className={styles.tagItemLabel}>
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={() => {
                                                    if (checked) {
                                                        onSelectedTagsChange(selectedTags.filter(n => n !== t.name))
                                                    } else {
                                                        onSelectedTagsChange([...selectedTags, t.name])
                                                    }
                                                }}
                                            />
                                            <span>{t.name}</span>
                                        </label>
                                    )
                                })}
                            </div>
                        </div>)}
                    <div className={styles.filtersFooter}>
                        <div className={styles.savedFiltersFrame}>
                            <SavedFiltersSelect />
                            <SavedFiltersButton />
                        </div>
                        <div className={styles.filtersActions}>
                            <button
                                type="button"
                                className="secondary"
                                onClick={() => {
                                    setPendingTags([])
                                    onSelectedTagsChange([])
                                    onConnectionChange([])
                                    onStatusChange('all')
                                    onMethodChange('all')
                                    onEnvironmentChange('all')
                                }}>Clear</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default ToolsFilters
